// src/lib/api_ai_marketing.ts
// Cliente para el módulo AI Marketing del backend Django.
// Mirrors apps/ai_marketing/schemas.py — /admin/ai-marketing/**.

const DJANGO_PROXY = '/django-api';

import { ensureFreshToken } from '@/lib/tokenRefresh';

async function getJwt(): Promise<string> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  if (!token) throw new Error('Sesión expirada. Inicia sesión nuevamente.');
  return token;
}

async function authHeaders(): Promise<Record<string, string>> {
  return {
    Authorization: `Bearer ${await getJwt()}`,
  };
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function djangoFetch<T = any>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = await authHeaders();
  if (init?.body !== undefined && !(init?.headers && (init.headers as Record<string, string>)['Content-Type'])) { headers['Content-Type'] = 'application/json'; }
  const url = `${DJANGO_PROXY}${path}`;
  const res = await fetch(url, { ...init, headers: { ...headers, ...(init?.headers || {}) } });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const detail = err.detail || `Django ${res.status}: ${res.statusText}`;
    if (res.status === 401) throw new ApiError('Sesión expirada.', 401);
    if (res.status === 403) throw new ApiError(detail || 'Sin permisos.', 403);
    if (res.status === 404) throw new ApiError(detail || 'Recurso no encontrado.', 404);
    if (res.status === 422) throw new ApiError(detail || 'Error de validación.', 422);
    throw new ApiError(typeof detail === 'string' ? detail : JSON.stringify(detail), res.status);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

// ═══════════════════════════════════════════════════════════════
// Types — mirror apps/ai_marketing/schemas.py
// ═══════════════════════════════════════════════════════════════

export interface Paginated<T> { items: T[]; count: number }

export type GenerationStatus = 'pending' | 'completed' | 'failed';

export type ContentKind =
  | 'caption' | 'headline' | 'ad_copy'
  | 'hashtag_set' | 'cta' | 'long_form' | 'ad_variants';

export type TargetPlatform =
  | 'generic' | 'facebook' | 'instagram' | 'google'
  | 'tiktok' | 'email' | 'whatsapp';

export type LLMProviderKind = '' | 'anthropic' | 'openai';

export interface ContentGeneration {
  id: number;
  org_id: string;
  campaign_id: number | null;
  scheduled_post_id: number | null;
  content_type: ContentKind;
  target_platform: TargetPlatform;
  prompt: string;
  system_prompt: string;
  context_data: Record<string, unknown>;
  requested_variants: number;
  provider: LLMProviderKind;
  model: string;
  status: GenerationStatus;
  output_text: string;
  output_variants: string[];
  error_message: string;
  input_tokens: number | null;
  output_tokens: number | null;
  total_tokens: number | null;
  created_at: string;
  updated_at: string;
}

export interface GenerateContentIn {
  content_type?: ContentKind;
  target_platform?: TargetPlatform;
  prompt: string;
  context_data?: Record<string, unknown>;
  campaign_id?: number | null;
  scheduled_post_id?: number | null;
  system_prompt?: string;
  requested_variants?: number;
  max_tokens?: number;
  temperature?: number;
  model?: string | null;
}

export interface AudienceSuggestion {
  id: number;
  org_id: string;
  campaign_id: number | null;
  prompt: string;
  context_data: Record<string, unknown>;
  suggested_name: string;
  suggested_targeting: Record<string, unknown>;
  reasoning: string;
  confidence: number | null;
  provider: LLMProviderKind;
  model: string;
  status: GenerationStatus;
  error_message: string;
  input_tokens: number | null;
  output_tokens: number | null;
  total_tokens: number | null;
  applied: boolean;
  applied_at: string | null;
  dismissed: boolean;
  created_at: string;
  updated_at: string;
}

export interface BuildAudienceIn {
  prompt: string;
  context_data?: Record<string, unknown>;
  campaign_id?: number | null;
  max_tokens?: number;
}

export type OptimizationCategory =
  | 'budget' | 'pause_adset' | 'rotate_creative' | 'change_objective'
  | 'extend_duration' | 'new_audience' | 'other';

export interface OptimizationLog {
  id: number;
  org_id: string;
  campaign_id: number;
  cycle_id: string;           // UUID
  category: OptimizationCategory;
  suggestion_text: string;
  action_payload: Record<string, unknown>;
  reasoning: string;
  confidence: number | null;
  provider: LLMProviderKind;
  model: string;
  applied: boolean;
  applied_at: string | null;
  dismissed: boolean;
  created_at: string;
}

export interface OptimizationTriggerOut {
  org_id: string;
  cycle_id: string | null;
  task_id: string | null;
  status: string;
  detail?: string;
}

// ── Query-string helper ──────────────────────────────────────

function qs(params: Record<string, unknown>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

// ═══════════════════════════════════════════════════════════════
// Content generation (A10)
// ═══════════════════════════════════════════════════════════════

export async function generateContent(data: GenerateContentIn): Promise<ContentGeneration> {
  return djangoFetch<ContentGeneration>('/api/v1/admin/ai-marketing/content/generate', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function listContent(
  filters: {
    content_type?: ContentKind; target_platform?: TargetPlatform;
    status?: GenerationStatus; campaign_id?: number;
    limit?: number; offset?: number;
  } = {},
): Promise<Paginated<ContentGeneration>> {
  const { limit = 25, offset = 0, ...rest } = filters;
  return djangoFetch<Paginated<ContentGeneration>>(
    `/api/v1/admin/ai-marketing/content${qs({ ...rest, limit, offset })}`,
  );
}

export async function getContent(id: number): Promise<ContentGeneration> {
  return djangoFetch<ContentGeneration>(`/api/v1/admin/ai-marketing/content/${id}`);
}

// ═══════════════════════════════════════════════════════════════
// Audience suggestions (A10)
// ═══════════════════════════════════════════════════════════════

export async function buildAudience(data: BuildAudienceIn): Promise<AudienceSuggestion> {
  return djangoFetch<AudienceSuggestion>('/api/v1/admin/ai-marketing/audience/build', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function listAudiences(
  filters: {
    applied?: boolean; dismissed?: boolean;
    campaign_id?: number; status?: GenerationStatus;
    limit?: number; offset?: number;
  } = {},
): Promise<Paginated<AudienceSuggestion>> {
  const { limit = 25, offset = 0, ...rest } = filters;
  return djangoFetch<Paginated<AudienceSuggestion>>(
    `/api/v1/admin/ai-marketing/audiences${qs({ ...rest, limit, offset })}`,
  );
}

export async function applyAudience(id: number): Promise<AudienceSuggestion> {
  return djangoFetch<AudienceSuggestion>(
    `/api/v1/admin/ai-marketing/audiences/${id}/apply`,
    { method: 'POST', body: '{}' },
  );
}

export async function dismissAudience(id: number): Promise<AudienceSuggestion> {
  return djangoFetch<AudienceSuggestion>(
    `/api/v1/admin/ai-marketing/audiences/${id}/dismiss`,
    { method: 'POST', body: '{}' },
  );
}

// ═══════════════════════════════════════════════════════════════
// Optimizer (A11)
// ═══════════════════════════════════════════════════════════════

export async function triggerOptimizer(): Promise<OptimizationTriggerOut> {
  return djangoFetch<OptimizationTriggerOut>(
    '/api/v1/admin/ai-marketing/optimizer',
    { method: 'POST', body: '{}' },
  );
}

export async function listAlerts(
  filters: {
    campaign_id?: number; category?: OptimizationCategory;
    applied?: boolean; dismissed?: boolean; cycle_id?: string;
    limit?: number; offset?: number;
  } = {},
): Promise<Paginated<OptimizationLog>> {
  const { limit = 50, offset = 0, ...rest } = filters;
  return djangoFetch<Paginated<OptimizationLog>>(
    `/api/v1/admin/ai-marketing/alerts${qs({ ...rest, limit, offset })}`,
  );
}

export async function applyAlert(id: number): Promise<OptimizationLog> {
  return djangoFetch<OptimizationLog>(
    `/api/v1/admin/ai-marketing/alerts/${id}/apply`,
    { method: 'POST', body: '{}' },
  );
}

export async function dismissAlert(id: number): Promise<OptimizationLog> {
  return djangoFetch<OptimizationLog>(
    `/api/v1/admin/ai-marketing/alerts/${id}/dismiss`,
    { method: 'POST', body: '{}' },
  );
}
